/**
 * The full set of icons available to the `IconRadioGroup` control.
 *
 * Keys are PascalCase accessors (e.g. `IconRadioGroup.Icon.AlignLeft`); values
 * are the public icon name in lowercase kebab-case (e.g. `'align-left'`), so a
 * bare string works too: `icon: 'align-left'`. This matches the convention used
 * for component icons in `registerComponent` (e.g. `icon: 'social-links'`).
 *
 * Brand/company logos are namespaced from the generic UI icons with a `Logo`
 * key prefix and a matching `logo-` value prefix (e.g.
 * `IconRadioGroup.Icon.LogoApple` -> `'logo-apple'`), which also keeps them
 * grouped together in a single block.
 *
 * The runtime is intentionally decoupled from the builder's icon component
 * filenames — the builder maps these names to its own icons (e.g. `'align-left'`
 * -> `AlignLeft16`), the same way Component Icons are handled.
 */
export const icons = {
  Activity: 'activity',
  AlignCenter: 'align-center',
  AlignLeft: 'align-left',
  AlignRight: 'align-right',
  ArrowCenter: 'arrow-center',
  ArrowDownUp: 'arrow-down-up',
  ArrowInside: 'arrow-inside',
  ArrowOutside: 'arrow-outside',
  ArrowRight: 'arrow-right',
  ArrowsUpDown: 'arrows-up-down',
  AspectStandard: 'aspect-standard',
  AspectWide: 'aspect-wide',
  Audio: 'audio',
  Billing: 'billing',
  BlankPage: 'blank-page',
  BorderDashed: 'border-dashed',
  BorderDotted: 'border-dotted',
  BorderSolid: 'border-solid',
  ButtonPill: 'button-pill',
  ButtonRounded: 'button-rounded',
  ButtonSquare: 'button-square',
  Chat: 'chat',
  ChatAi: 'chat-ai',
  Checkbox: 'checkbox',
  Checkmark: 'checkmark',
  Clock: 'clock',
  Code: 'code',
  Cog: 'cog',
  Connection: 'connection',
  Corners: 'corners',
  CountdownSolid: 'countdown-solid',
  Cube: 'cube',
  Database: 'database',
  Deny: 'deny',
  Desktop: 'desktop',
  Document: 'document',
  Download: 'download',
  Drop: 'drop',
  Dropdown: 'dropdown',
  Duplicate: 'duplicate',
  Ellipsis: 'ellipsis',
  Email: 'email',
  Eye: 'eye',
  File: 'file',
  FileUpload: 'file-upload',
  Folder: 'folder',
  FolderArrow: 'folder-arrow',
  FolderUpload: 'folder-upload',
  Gallery: 'gallery',
  Globe: 'globe',
  GlobeAi: 'globe-ai',
  GradientLinear: 'gradient-linear',
  GradientRadial: 'gradient-radial',
  Graph: 'graph',
  GridCircles: 'grid-circles',
  HeightAuto: 'height-auto',
  HeightMatch: 'height-match',
  Help: 'help',
  HelpCircle: 'help-circle',
  Hidden: 'hidden',
  Image: 'image',
  Info: 'info',
  Italic: 'italic',
  Key: 'key',
  Keyboard: 'keyboard',
  Link: 'link',
  LogoAngellist: 'logo-angellist',
  LogoApple: 'logo-apple',
  LogoBigCommerce: 'logo-bigcommerce',
  LogoBynder: 'logo-bynder',
  LogoCodepen: 'logo-codepen',
  LogoCodesandbox: 'logo-codesandbox',
  LogoDiscord: 'logo-discord',
  LogoDribbble: 'logo-dribbble',
  LogoFacebook: 'logo-facebook',
  LogoGithub: 'logo-github',
  LogoGoogle: 'logo-google',
  LogoInstagram: 'logo-instagram',
  LogoLinkedin: 'logo-linkedin',
  LogoMastodon: 'logo-mastodon',
  LogoMedium: 'logo-medium',
  LogoMessenger: 'logo-messenger',
  LogoNextjs: 'logo-nextjs',
  LogoNextjsOutline: 'logo-nextjs-outline',
  LogoNotion: 'logo-notion',
  LogoPinterest: 'logo-pinterest',
  LogoReddit: 'logo-reddit',
  LogoSlack: 'logo-slack',
  LogoSnapchat: 'logo-snapchat',
  LogoSoundcloud: 'logo-soundcloud',
  LogoSpotify: 'logo-spotify',
  LogoTelegram: 'logo-telegram',
  LogoTiktok: 'logo-tiktok',
  LogoTwitch: 'logo-twitch',
  LogoVimeo: 'logo-vimeo',
  LogoWhatsapp: 'logo-whatsapp',
  LogoX: 'logo-x',
  LogoYelp: 'logo-yelp',
  LogoYoutube: 'logo-youtube',
  Logout: 'logout',
  Minus: 'minus',
  Mobile: 'mobile',
  Moon: 'moon',
  MultiSelect: 'multi-select',
  Number: 'number',
  Offline: 'offline',
  Pencil: 'pencil',
  Percent: 'percent',
  Phone: 'phone',
  Plus: 'plus',
  Popout: 'popout',
  Publish: 'publish',
  Px: 'px',
  RadioButton: 'radio-button',
  Redirect: 'redirect',
  Redo: 'redo',
  Rename: 'rename',
  RepeatX: 'repeat-x',
  RepeatXy: 'repeat-xy',
  RepeatY: 'repeat-y',
  Reset: 'reset',
  Retry: 'retry',
  Rss: 'rss',
  Search: 'search',
  Settings: 'settings',
  ShadowInner: 'shadow-inner',
  ShadowOuter: 'shadow-outer',
  Share: 'share',
  SidebarClosed: 'sidebar-closed',
  SidebarOpen: 'sidebar-open',
  Sides: 'sides',
  Sparkles: 'sparkles',
  Star: 'star',
  StarCircle: 'star-circle',
  Strikethrough: 'strikethrough',
  Subscript: 'subscript',
  Sun: 'sun',
  Superscript: 'superscript',
  Swap: 'swap',
  Tablet: 'tablet',
  Template: 'template',
  TestTube: 'test-tube',
  Text: 'text',
  TextAi: 'text-ai',
  TextAlignCenter: 'text-align-center',
  TextAlignJustify: 'text-align-justify',
  TextAlignLeft: 'text-align-left',
  TextAlignRight: 'text-align-right',
  TextInput: 'text-input',
  Times: 'times',
  Trash: 'trash',
  Underline: 'underline',
  Undo: 'undo',
  Upload: 'upload',
  Uppercase: 'uppercase',
  User: 'user',
  Users: 'users',
  VerticalAlignEnd: 'vertical-align-end',
  VerticalAlignMiddle: 'vertical-align-middle',
  VerticalAlignSpaceBetween: 'vertical-align-space-between',
  VerticalAlignStart: 'vertical-align-start',
  Video: 'video',
  Warning: 'warning',
} as const

/**
 * Legacy icon ids serialized by runtimes from before icon names were
 * kebab-cased — notably the released `unstable_IconRadioGroup` and the
 * built-in RichText toolbars that used it. `deserialize` still accepts these
 * so panels hosted on older runtimes keep parsing; the builder maps them to
 * its icons.
 *
 * The `TextAlignPlugin` toolbar always serialized unsuffixed PascalCase ids
 * (`TextAlignLeft`, ...). The `InlinePlugin` toolbar serialized two forms: the
 * builder's `*16` component filenames (`Code16`, ...) from the released
 * `unstable_IconRadioGroup`, and unsuffixed PascalCase ids (`Code`,
 * `Subscript`, `Superscript`) from the stabilized-but-pre-kebab `IconRadioGroup`
 * published on the `canary` channel. All forms are accepted here.
 */
export const legacyIcons = {
  Code: 'Code',
  Code16: 'Code16',
  Subscript: 'Subscript',
  Subscript16: 'Subscript16',
  Superscript: 'Superscript',
  Superscript16: 'Superscript16',
  TextAlignCenter: 'TextAlignCenter',
  TextAlignJustify: 'TextAlignJustify',
  TextAlignLeft: 'TextAlignLeft',
  TextAlignRight: 'TextAlignRight',
} as const
